import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'

import styles from './styles.module.scss'

import { getPlaceFolders, createPlaceFolder, updatePlaceFolder, deletePlaceFolder, getCompanyEmployeesAll } from '../../../api'
import { generateResourcesFromEmployees } from '../../../helpers/generateResourcesFromEmployees'

import PlusCircleIcon from '../../Icons/PlusCircle'
import PlaceIcon from '../../Icons/Place'
import FolderItem from '../FolderItem'
import FolderContent from '../FolderContent'
import DocumentIcon from '../../Icons/DocumentIcon'
import PlaceDescription from '../PlaceDescription'
import ConfirmationModal from '../../../components/Core/Dialog/ConfirmationModal'

const createFolderHover = {
  id: 0,
  editable: true,
  name: '',
  description: '',
}

const Documents = ({companyId, placeId, selectedPlace, canManage, onUpdatedDescription}) => {
  const { t } = useTranslation()

  const [folders, setFolders] = useState({loading: false, list: []})
  const [selectedFolderId, setSelectedFolderId] = useState(null)
  const [showDescription, setShowDescription] = useState(false)
  const [employees, setEmployees] = useState([])

  const folderContentRef = useRef()
  const confirmationModalRef = useRef()

  useEffect(() => {
    if (companyId && placeId) {
      getFolders()
      getEmployees()
    }
    return () => {
      setSelectedFolderId(null)
      setShowDescription(false)
    }
  }, [companyId, placeId])

  const getEmployees = async () => {
    const res = await getCompanyEmployeesAll(companyId)
    if (Array.isArray(res?.users)) {
      const groupedEmployees = generateResourcesFromEmployees(res.users)
      setEmployees(groupedEmployees)
    }
  }

  const getFolders = async () => {
    setFolders({loading: true, list: []})
    const res = await getPlaceFolders(companyId, placeId)
    if (Array.isArray(res?.data)) {
      setFolders({loading: false, list: res.data.map(f => ({...f, description: f.description ?? ''}))})
      return
    }
    setFolders({loading: false, list: []})
  }

  const handleAddFolder = () => {
    const exist = folders.list.find(f => f.id === createFolderHover.id)
    if (exist) {
      return
    }
    setFolders(prev => ({...prev, list: [{...createFolderHover}, ...prev.list]}))
  }

  const handleSubmitFolder = async (folder) => {
    if (folder.id) {
      const res = await updatePlaceFolder(companyId, placeId, folder.id, {name: folder.name, description: folder.description})
      if (res?.data) {
        const updatedFolder = {...res.data, description: res.data.description ?? ''}
        setFolders(prev => ({...prev, list: prev.list.map(f => f.id === folder.id ? updatedFolder : f)}))
        if (folderContentRef.current) {
          folderContentRef.current.update({name: res.data.name, description: res.data.description})
        }
      }
      return
    }
    const res = await createPlaceFolder(companyId, placeId, {name: folder.name, description: folder.description})
    if (res?.data) {
      const newFolder = {...res.data, description: res.data.description ?? ''}
      setFolders(prev => ({...prev, list: prev.list.map(f => f.id === folder.id ? newFolder : f)}))
    }
  }

  const handleCancelFolder = (folderId) => {
    if (!folderId) {
      setFolders(prev => ({...prev, list: prev.list.filter(f => f.id !== folderId)}))
      return
    }
    setFolders(prev => ({...prev, list: prev.list.map(f => f.id === folderId ? {...f, editable: false} : f)}))
  }

  const handleEditFolder = (folderId) => {
    setFolders(prev => ({...prev, list: prev.list.map(f =>  ({...f, editable: f.id === folderId})).filter(f => Boolean(f.id))}))
  }

  const handleRemoveFolder = async (folder) => {
    const result = await confirmationModalRef.current.show({
      title: t('Delete {{folder_name}}?', {folder_name: folder.name}),
      description: t('This action will delete this folder and all the files in it.'),
    })
    if (!result.success) {
      return
    }
    const res = await deletePlaceFolder(companyId, placeId, folder.id)
    if (res) {
      setFolders(prev => ({...prev, list: prev.list.filter(f => f.id !== folder.id)}))
      if (selectedFolderId === folder.id) {
        setSelectedFolderId(null)
      }
    }
  }

  const handleSelectFolder = (folderId) => {
    setSelectedFolderId(prev => prev === folderId ? null : folderId)
    setShowDescription(false)
  }

  const handleUpdateFolder = (updates) => {
    setFolders(prev => ({...prev, list: prev.list.map(f => f.id === updates.id ? {...f, ...updates} : f)}))
  }

  const handleShowDescription = () => {
    setSelectedFolderId(null)
    setShowDescription(true)
  }

  if (!placeId) {
    return (
      <div className={styles.emptyContainer}>
        <PlaceIcon width={67} height={77} color="#0789FF" className={styles.placeIcon} />
        <div style={{textTransform: 'uppercase'}}>{ t('Nothing selected') }</div>
        <div>{ t('Please select a project or a place to get detailed information') }</div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.folders}>
        <div className={styles.foldersHeader}>
          <div className={styles.title}>{ t('Folders') }</div>
          {
            canManage
              ? <div className={styles.addButton} onClick={handleAddFolder}>
                  <PlusCircleIcon />
                </div>
              : null
          }
        </div>
        {
          canManage || selectedPlace?.folder_description
            ? <div className={cn(styles.description, styles.editable, {[styles.active]: showDescription})} onClick={handleShowDescription}>
                <div className={styles.descriptionTitle}>{ t('description') }</div>
                {
                  canManage
                    ? <div className={styles.descriptionDot} style={{backgroundColor: selectedPlace?.folder_description ? '#13D246' : '#D21313'}} />
                    : null
                }
              </div>
            : null
        }
        <div className={styles.foldersList}>
          {
            folders.list.map(folder => {
              return (
                <FolderItem
                  key={folder.id}
                  folder={folder}
                  isSelected={selectedFolderId === folder.id}
                  canManage={canManage}
                  onSelect={handleSelectFolder}
                  onSubmit={handleSubmitFolder}
                  onCancel={handleCancelFolder}
                  onEdit={handleEditFolder}
                  onRemove={handleRemoveFolder} />
              )
            })
          }
          {
            !folders.list.length
              ? <div className={styles.emptyFolders}>
                  <DocumentIcon />
                  { t('No folders created yet') }
                </div>
              : null
          }
        </div>
      </div>
      <div className={styles.folderContent}>
        {
          selectedFolderId
            ? <FolderContent
                ref={folderContentRef}
                companyId={companyId}
                placeId={placeId}
                folderId={selectedFolderId}
                canManage={canManage}
                employees={employees}
                onUpdate={handleUpdateFolder} />
            : !showDescription
              ? <div className={styles.emptyContainer}>
                  <DocumentIcon width={67} height={77} color="#0789FF" className={styles.placeIcon} />
                  <div style={{textTransform: 'uppercase'}}>{ t('Nothing selected') }</div>
                  <div>{ t('Please select a folder first to get detailed information') }</div>
                </div>
              : <PlaceDescription
                  companyId={companyId}
                  placeId={placeId}
                  canManage={canManage}
                  description={selectedPlace?.folder_description || ''}
                  onUpdated={onUpdatedDescription} />
        }
      </div>
      <ConfirmationModal ref={confirmationModalRef} />
    </div>
  )
}

export default Documents
